import _ from "lodash";
import i18n from "@/shared/i18n";
import {store} from "@/plugins/vuex";
import {DPV_TABLE_NAME, SOURCE_DPV_HISTORY_TABLE, SOURCE_AUCTION_DPV_TABLE} from "../../constants";
import DpvSearch from "../DpvSearch.model";
import DpvService from "../DpvService";

let getDefaultTableData = (differentFields = []) => {
  return {
    loading: false,
    rows: [],
    pagination: {
      limit: 50,
      page: 1,
      total: 0,
    },
    ordering: '-created_at',
    search: '',
    searchModel: new DpvSearch(),
    toggleColumns: {
      fields: [...getDefaultFields(), ...differentFields],
      requiredFields: ['dpv_no'],
      defaultFields: ['sale_number', 'sale_start_date', 'buyer_number', 'buyer_full_name', 'invoice_number', 'dpv_no',
        'total_hammer', 'description', 'pdf_path', 'created_at'],
    }
  }
};

let getDefaultAuctionDpvTableData = (differentFields = []) => {
  return {
    loading: false,
    rows: [],
    pagination: {
      limit: 50,
      page: 1,
      total: 0,
    },
    ordering: '-created_at',
    search: '',
    searchModel: new DpvSearch(),
    auctionUuid: null,
    toggleColumns: {
      fields: [...getDefaultAuctionDpvFields(), ...differentFields],
      requiredFields: ['dpv_no'],
      defaultFields: ['buyer_number', 'buyer_full_name', 'invoice_number', 'dpv_no', 'total_hammer', 'description',
        'pdf_path', 'created_at'],
    }
  }
};

let getDefaultFields = () => {
  return [
    {
      key: 'sale_number',
      label: i18n.t('Sale'),
      sortable: true,
      isVisible: true
    },
    {
      key: 'sale_start_date',
      label: i18n.t('Sale Start - End Date'),
      isVisible: true
    },
    {
      key: 'buyer_number',
      label: i18n.t('Buyer Number'),
      isVisible: true
    },
    {
      key: 'buyer_full_name',
      label: i18n.t('Buyer'),
      isVisible: true
    },
    {
      key: 'invoice_number',
      label: i18n.t('Invoice'),
      isVisible: true
    },
    {
      key: 'dpv_no',
      label: i18n.t('DPV Number'),
      isVisible: true
    },
    {
      key: 'total_hammer',
      label: i18n.t('Total Hammer'),
      isVisible: true
    },
    {
      key: 'description',
      label: i18n.t('DPV Description'),
      isVisible: true,
      tdClass: 'dpv-description-col'
    },
    {
      key: 'pdf_path',
      label: i18n.t('View PDF'),
      isVisible: true,
      exclude_from_export: true
    },
    {
      key: 'created_at',
      label: i18n.t('Created At Date'),
      isVisible: true
    }
  ];
};

let getDefaultAuctionDpvFields = () => {
  return [
    {
      key: 'buyer_number',
      label: i18n.t('Buyer Number'),
      isVisible: true
    },
    {
      key: 'buyer_full_name',
      label: i18n.t('Buyer'),
      isVisible: true
    },
    {
      key: 'invoice_number',
      label: i18n.t('Invoice'),
      isVisible: true
    },
    {
      key: 'dpv_no',
      label: i18n.t('DPV Number'),
      isVisible: true
    },
    {
      key: 'total_hammer',
      label: i18n.t('Total Hammer'),
      isVisible: true
    },
    {
      key: 'description',
      label: i18n.t('DPV Description'),
      isVisible: true,
      tdClass: 'dpv-description-col'
    },
    {
      key: 'pdf_path',
      label: i18n.t('View PDF'),
      isVisible: true,
      exclude_from_export: true
    },
    {
      key: 'created_at',
      label: i18n.t('Created At Date'),
      isVisible: true
    }
  ];
};

export default {
  namespaced: true,
  state: {
    tableSource: null,
    tableData: {
      [SOURCE_DPV_HISTORY_TABLE]: getDefaultTableData(),
      [SOURCE_AUCTION_DPV_TABLE]: getDefaultAuctionDpvTableData()
    },
    modal: {
      show: false
    },
    descriptionModal: {
      show: false,
      description: null
    }
  },
  actions: {
    showModal({commit}) {
      commit('showModal');
    },
    hideModal({commit}) {
      commit('hideModal');
    },
    showDescriptionModal({commit}, description) {
      commit('showDescriptionModal', description);
    },
    hideDescriptionModal({commit}) {
      commit('hideDescriptionModal');
    },
    resetTableDataPaginationPage({state}) {
      state.tableData[state.tableSource].pagination.page = 1;
    },
    updateSearchModel({commit, state}, searchModel) {
      commit('updateSearchModel', new DpvSearch({
        ...state.tableData[state.tableSource].searchModel,
        ...searchModel
      }));
    },
    async getTableData({commit, state, dispatch}, payload) {
      let params = await dispatch('getTableParams', payload);

      commit('updateTableLoading', true);
      const response = await DpvService.getDpvHistory(state.tableData[state.tableSource].auctionUuid, params);
      commit('updateTableLoading', false);

      if (response.success) {
        commit('updateTableData', {
          rows: response.body.results,
          pagination: {limit: params.limit, page: params.page, total: response.body.count},
          ordering: params.ordering,
          search: params.search
        });
      }

      return response;
    },
    async resetTable({state, dispatch}) {
      const tmpToggleColumns = _.cloneDeep(state.tableData[state.tableSource].toggleColumns);
      let auctionUuid = state.tableData[state.tableSource].auctionUuid;
      let defaultTableData = getDefaultTableData();
      defaultTableData.pagination.limit = state.tableData[state.tableSource].pagination.limit;
      state.tableData[state.tableSource] = defaultTableData;
      state.tableData[state.tableSource].toggleColumns = tmpToggleColumns;
      state.tableData[state.tableSource].auctionUuid = auctionUuid;
      dispatch('getTableData');
    },
    async getTableParams({state}, payload = null) {
      let data;
      let tableData = state.tableData[state.tableSource];
      const ordering = await store.dispatch('global/getTableDataOrdering', {
        tableData: tableData,
        tableName: `${DPV_TABLE_NAME}.${state.tableSource}`
      });

      if (payload) {
        data = {
          ...payload.pagination,
          search: payload.search,
          ordering: payload.ordering,
        };
      } else {
        data = {
          ...tableData.pagination,
          search: tableData.search,
          ordering: ordering,
        }
      }

      if (tableData.searchModel) {
        const searchModelData = tableData.searchModel.toJSON();
        data = {
          ...data,
          ...searchModelData
        }
      }

      return data;
    },
    async updateTableSource({state}, {source, auctionUuid = null, clearTableData = false}) {
      state.tableSource = source;
      if (clearTableData) {
        if (source === SOURCE_DPV_HISTORY_TABLE) {
          state.tableData[source] = getDefaultTableData();
        }
        if (source === SOURCE_AUCTION_DPV_TABLE) {
          state.tableData[source] = getDefaultAuctionDpvTableData();
        }
      }
      state.tableData[source].auctionUuid = auctionUuid;
    },
    async updateToggleColumns({commit, dispatch}, payload) {
      if (payload.isSaveAction) {
        commit('updateTableLoading', true);
        await commit('updateToggleColumns', payload.selectedColumns);
        await dispatch('getTableData');
        commit('updateTableLoading', false);
      } else {
        await commit('updateToggleColumns', payload.selectedColumns);
      }
    }
  },
  mutations: {
    showModal: state => state.modal.show = true,
    hideModal: state => state.modal.show = false,
    updateTableData: (state, payload) => state.tableData[state.tableSource] = {...state.tableData[state.tableSource], ...payload},
    updateTableLoading: (state, payload) => state.tableData[state.tableSource].loading = payload,
    updateSearchModel: (state, searchModel) => state.tableData[state.tableSource].searchModel = searchModel,
    updateToggleColumns: (state, selectedColumns) => {
      state.tableData[state.tableSource].toggleColumns.defaultFields = [...selectedColumns];
      let selectedFields = [];
      let notSelectedFields = [];
      selectedColumns.forEach(s => {
        selectedFields.push({
          ...state.tableData[state.tableSource].toggleColumns.fields.find(f => f.key === s),
          isVisible: true
        });
      });
      state.tableData[state.tableSource].toggleColumns.fields.forEach(f => {
        if (!selectedColumns.includes(f.key)) {
          notSelectedFields.push({...f, isVisible: false});
        }
      });
      state.tableData[state.tableSource].toggleColumns.fields = [...selectedFields, ...notSelectedFields];
    },
    showDescriptionModal: (state, description) => {
      state.descriptionModal.description = description;
      state.descriptionModal.show = true;
    },
    hideDescriptionModal: (state) => {
      state.descriptionModal.description = null;
      state.descriptionModal.show = false;
    }
  }
}
